import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
} from '@mui/material';
import {
  CloudUpload,
  FolderOpen,
  InsertDriveFile,
  Close,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const MAX_FILES = 100;
const MAX_FILE_SIZE_BYTES = 16 * 1024 * 1024; // 16MB - MongoDB BSON document size limit
const SUPPORTED_EXTENSIONS = ['.txt', '.md', '.pdf', '.docx', '.doc'];

interface FileWithPath {
  file: File;
  path: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface FileUploadProps {
  onUploadComplete: () => void;
}

export default function FileUpload({ onUploadComplete }: FileUploadProps) {
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const isFileSupported = useCallback((filename: string): boolean => {
    const ext = '.' + filename.toLowerCase().split('.').pop();
    return SUPPORTED_EXTENSIONS.includes(ext);
  }, []);

  const validateFiles = useCallback((fileList: FileList): { valid: FileWithPath[]; errors: string[] } => {
    const errors: string[] = [];
    const validFiles: FileWithPath[] = [];

    if (fileList.length > MAX_FILES) {
      errors.push(`Maximum ${MAX_FILES} files per upload. Please select fewer files or split into batches.`);
      return { valid: [], errors };
    }

    Array.from(fileList).forEach((file) => {
      const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;

      if (!isFileSupported(file.name)) {
        errors.push(`${file.name}: Unsupported file type. Supported: ${SUPPORTED_EXTENSIONS.join(', ')}`);
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        errors.push(`${file.name}: File size (${sizeMB}MB) exceeds maximum limit of 16MB`);
        return;
      }

      validFiles.push({
        file,
        path: relativePath,
        status: 'pending',
      });
    });

    return { valid: validFiles, errors };
  }, [isFileSupported]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    const { valid, errors } = validateFiles(fileList);

    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setFiles(valid);
    setError(null);
  }, [validateFiles]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const fileList = event.dataTransfer.files;
    if (!fileList || fileList.length === 0) return;

    const { valid, errors } = validateFiles(fileList);

    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setFiles(valid);
    setError(null);
  }, [validateFiles]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const clearFiles = () => {
    setFiles([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach(({ file }) => {
        formData.append('files', file);
      });

      const response = await axios.post(API_ENDPOINTS.context.upload, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        },
      });

      // Update file statuses based on response
      const { results, errors: uploadErrors } = response.data;

      setFiles((prev) =>
        prev.map((f) => {
          const success = results?.find((r: { filename: string }) => r.filename === f.file.name);
          const error = uploadErrors?.find((e: { filename: string }) => e.filename === f.file.name);

          if (success) {
            return { ...f, status: 'success' };
          } else if (error) {
            return { ...f, status: 'error', error: error.error };
          }
          return f;
        })
      );

      if (uploadErrors && uploadErrors.length > 0) {
        setError(`${uploadErrors.length} file(s) failed to upload. See details below.`);
      } else {
        // Show success message only if all files uploaded successfully
        const successCount = results?.length || files.length;
        setSuccessMessage(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}!`);
      }

      // Call completion callback to refresh document list
      onUploadComplete();

      // Clear successful files after a delay
      setTimeout(() => {
        setFiles((prev) => prev.filter((f) => f.status === 'error'));
      }, 3000);
    } catch (err) {
      console.error('Upload failed:', err);
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Upload failed. Please try again.');
      }

      // Mark all files as error
      setFiles((prev) => prev.map((f) => ({ ...f, status: 'error', error: 'Upload failed' })));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Box>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Stack spacing={2}>
        {/* Drop zone */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: '2px dashed #E2E8F0',
            borderRadius: '12px',
            p: 4,
            textAlign: 'center',
            backgroundColor: '#F8FAFC',
            transition: 'all 0.2s',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#667eea',
              backgroundColor: '#F1F5F9',
            },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUpload sx={{ fontSize: 48, color: '#94A3B8', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Drop files here or click to browse
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Supported formats: {SUPPORTED_EXTENSIONS.join(', ')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Maximum {MAX_FILES} files per upload
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Maximum 16MB per file
          </Typography>
        </Box>

        {/* Action buttons */}
        <Stack direction="row" spacing={2}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={SUPPORTED_EXTENSIONS.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <input
            ref={folderInputRef}
            type="file"
            // @ts-expect-error - webkitdirectory is not in TypeScript types
            webkitdirectory="true"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          <Button
            variant="outlined"
            startIcon={<InsertDriveFile />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Select Files
          </Button>
          <Button
            variant="outlined"
            startIcon={<FolderOpen />}
            onClick={() => folderInputRef.current?.click()}
            disabled={uploading}
          >
            Select Folder
          </Button>
          {files.length > 0 && (
            <Button variant="outlined" color="error" onClick={clearFiles} disabled={uploading}>
              Clear All
            </Button>
          )}
        </Stack>

        {/* Error display */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{error}</pre>
          </Alert>
        )}

        {/* File list */}
        {files.length > 0 && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Selected Files ({files.length}/{MAX_FILES})
              </Typography>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading || files.length === 0}
                startIcon={<CloudUpload />}
              >
                {uploading ? `Uploading ${uploadProgress}%` : 'Upload Files'}
              </Button>
            </Stack>

            {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />}

            <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
              {files.map((fileWithPath, index) => (
                <ListItem key={index} divider={index < files.length - 1}>
                  <ListItemIcon>
                    {fileWithPath.status === 'success' ? (
                      <CheckCircle color="success" />
                    ) : fileWithPath.status === 'error' ? (
                      <ErrorIcon color="error" />
                    ) : (
                      <InsertDriveFile color="action" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={fileWithPath.path}
                    secondary={
                      fileWithPath.error
                        ? fileWithPath.error
                        : fileWithPath.file.size > 1024 * 1024
                        ? `${(fileWithPath.file.size / (1024 * 1024)).toFixed(2)} MB`
                        : `${(fileWithPath.file.size / 1024).toFixed(1)} KB`
                    }
                    secondaryTypographyProps={{
                      color: fileWithPath.error ? 'error' : 'text.secondary',
                    }}
                  />
                  <ListItemSecondaryAction>
                    {fileWithPath.status === 'success' && <Chip label="Uploaded" color="success" size="small" />}
                    {fileWithPath.status === 'error' && <Chip label="Failed" color="error" size="small" />}
                    {fileWithPath.status === 'pending' && !uploading && (
                      <IconButton edge="end" size="small" onClick={() => removeFile(index)}>
                        <Close />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
